import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import 'iconify-icon';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { resolveState } from '../data/brazil-state-centroids';
import { franchiseWhatsApp } from '../components/franchise/franchise-shared';
import { useGlobalWaLink } from '../contexts/SettingsContext';
import { FranchiseHero } from '../components/franchise/FranchiseHero';
import { FranchiseProducts } from '../components/franchise/FranchiseProducts';
import { FranchiseTeam } from '../components/franchise/FranchiseTeam';
import { FranchiseAgenda } from '../components/franchise/FranchiseAgenda';
import {
  FranchiseTestimonials,
  FranchiseCases,
  FranchiseFinalCTA,
} from '../components/franchise/FranchiseSocial';
import { FranchiseLegal } from '../components/franchise/FranchiseLegal';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const FranchisePage = () => {
  const { id } = useParams();
  const [franchise, setFranchise] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [fRes, eRes] = await Promise.all([
          axios.get(`${API_URL}/public/franchises/${id}`),
          axios.get(`${API_URL}/public/events?franchise_id=${id}`),
        ]);
        setFranchise(fRes.data);
        setEvents(Array.isArray(eRes.data) ? eRes.data : []);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
    window.scrollTo(0, 0);
  }, [id]);

  const globalWa = useGlobalWaLink();
  const wppLink = useMemo(() => franchiseWhatsApp(franchise, globalWa), [franchise, globalWa]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-orange"></div>
      </div>
    );
  }

  if (error || !franchise) {
    return (
      <>
        <Header />
        <SEO title="Franquia não encontrada" path={`/franquia/${id}`} />
        <main
          className="min-h-[70vh] pt-32 pb-24 text-center max-w-2xl mx-auto px-6"
          style={{ background: 'rgba(10, 10, 10, 0.78)' }}
        >
          <iconify-icon icon="solar:shop-linear" className="text-6xl text-accent-orange mb-4"></iconify-icon>
          <h1 className="text-3xl font-bold text-[#EAEAEA] mb-3">Franquia não encontrada</h1>
          <Link
            to="/franquias"
            className="inline-flex items-center gap-2 px-6 py-3 bg-accent-orange text-white rounded-full text-sm font-bold uppercase tracking-wider hover:bg-[#E55D00] transition-colors"
          >
            <iconify-icon icon="solar:arrow-left-linear"></iconify-icon>
            Ver todas as franquias
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const {
    name, city, state, contact_email, legal_name, whatsapp, phone, address,
    cities_served = [], testimonials = [], cases = [], team = [],
    products_offered = [],
  } = franchise;
  const stateMeta = resolveState(state);
  const cities = cities_served.length ? cities_served : [city].filter(Boolean);

  // Rich OG metadata
  const ogDescription =
    `${name || `IE ${city}`} — ${team.length || 0} profissionais ativos, ${products_offered.length || 0} programas, atendendo ${cities.join(', ')} (${stateMeta?.name || state}).`;
  const ogImage = franchise.image || undefined; // fallback to default SEO image

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: name || `IE ${city}`,
    legalName: legal_name || undefined,
    email: contact_email,
    telephone: whatsapp || phone || undefined,
    address: address
      ? { '@type': 'PostalAddress', streetAddress: address, addressRegion: stateMeta?.name || state, addressCountry: 'BR' }
      : { '@type': 'PostalAddress', addressRegion: stateMeta?.name || state, addressCountry: 'BR' },
    areaServed: cities,
    parentOrganization: {
      '@type': 'Organization',
      name: 'Instituto Eneagrama',
      url: 'https://ie-interactive.emergent.host',
    },
  };

  return (
    <div data-testid="franchise-page">
      <SEO
        title={`${name || `IE ${city}`} — Eneagrama em ${stateMeta?.name || state}`}
        description={ogDescription}
        path={`/franquia/${id}`}
        image={ogImage}
        type="business.business"
        jsonLd={jsonLd}
      />
      <Header />
      <main className="min-h-screen">
        <FranchiseHero franchise={franchise} wppLink={wppLink} />
        <FranchiseProducts productsOffered={products_offered} />
        <FranchiseTeam team={team} />
        <FranchiseAgenda events={events} wppLink={wppLink} />
        <FranchiseTestimonials testimonials={testimonials} />
        <FranchiseCases cases={cases} />
        <FranchiseFinalCTA franchise={franchise} wppLink={wppLink} />
        <FranchiseLegal franchise={franchise} />
      </main>
      <Footer />
    </div>
  );
};

export default FranchisePage;
